import type { Attachment } from 'svelte/attachments'

/**
 * Programmatically focus the element after it mounts.
 *
 * Use on elements with `tabindex="-1"` (or otherwise non-tab-focusable) so
 * keyboard handlers (Escape, etc.) registered on the element have a target —
 * the browser does not auto-focus right-click-opened menus.
 *
 * @example
 * ```svelte
 * <div role="menu" tabindex="-1" {@attach autofocus}>…</div>
 * ```
 */
export const autofocus: Attachment<HTMLElement> = (element) => {
	queueMicrotask(() => element.focus())
}

type TrackElementOpts = {
	/**
	 * Returns the element whose position and size should be tracked. Read
	 * each time the attachment re-runs, so reading reactive state here
	 * (e.g. `buttonEls[current]`) makes the attachment follow it.
	 */
	target: () => HTMLElement | null | undefined
	/**
	 * Element the indicator's `x` is measured relative to. Defaults to the
	 * indicator's `offsetParent` — set explicitly when the indicator and
	 * its positioning context aren't direct DOM neighbours.
	 */
	container?: () => HTMLElement | null | undefined
	/** Override the CSS custom property names written on the indicator. */
	vars?: { x?: string; width?: string }
}

/**
 * Track a target element's position and size, writing them as CSS custom
 * properties on the attached "indicator" element. Re-measures on resize
 * (target + container) and re-runs when `target()` returns a different
 * element — e.g. when the active tab changes.
 *
 * Defaults: `--indicator-x` (px, transform-x) and `--indicator-width` (px).
 *
 * Pair with a CSS block roughly like:
 *
 * ```css
 * .indicator {
 *   position: absolute;
 *   left: 0;
 *   bottom: 0;
 *   width: var(--indicator-width);
 *   transform: translateX(var(--indicator-x));
 *   transition: transform 220ms ease, width 220ms ease;
 * }
 * ```
 *
 * @example  // tab-bar underline that follows the active button
 * <span class="indicator" {@attach trackElement({
 *   target: () => buttonEls[current],
 *   container: () => navEl
 * })}></span>
 */
export function trackElement(opts: TrackElementOpts): Attachment<HTMLElement> {
	const xVar = opts.vars?.x ?? '--indicator-x'
	const wVar = opts.vars?.width ?? '--indicator-width'

	return (indicator) => {
		const target = opts.target()
		const container = opts.container?.() ?? (indicator.offsetParent as HTMLElement | null)
		if (!target || !container) return

		const update = () => {
			const t = target.getBoundingClientRect()
			const c = container.getBoundingClientRect()
			indicator.style.setProperty(xVar, `${t.left - c.left}px`)
			indicator.style.setProperty(wVar, `${t.width}px`)
		}
		update()

		const ro = new ResizeObserver(update)
		ro.observe(target)
		if (container !== target) ro.observe(container)
		return () => ro.disconnect()
	}
}
